import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only dialog for bulk-importing curated Norwegian recipes from popular sites
 * via the existing per-source search engine. Also contains the on/off toggle for
 * the weekly Monday auto-import scheduler.
 */
export default function BulkImportRecipesDialog({ open, onOpenChange, onImported }) {
  const [limit, setLimit] = useState(20);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [autoCfg, setAutoCfg] = useState(null);

  const loadCfg = useCallback(async () => {
    try {
      const res = await api.getRecipeAutoImportConfig();
      setAutoCfg(res.data);
    } catch { /* non-admin simply won't see the card */ }
  }, []);

  useEffect(() => {
    if (open) {
      setResult(null);
      loadCfg();
    }
  }, [open, loadCfg]);

  const saveCfg = async (patch) => {
    setAutoCfg((c) => ({ ...c, ...patch })); // optimistic
    try {
      const res = await api.setRecipeAutoImportConfig(patch);
      setAutoCfg((c) => ({ ...c, ...res.data }));
      toast.success(patch.enabled === false
        ? "Ukentlig auto-import slått av"
        : patch.enabled === true
          ? "Ukentlig auto-import slått på"
          : "Oppdatert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre");
      loadCfg();
    }
  };

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      toast.info(`Laster ned ${limit} oppskrifter... dette kan ta et par minutter`);
      const res = await api.bulkImportPopularRecipes(limit);
      setResult(res.data);
      toast.success(`${res.data.imported_count} oppskrifter lagt til (${res.data.failed_count} feilet)`);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk-import feilet");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!running) onOpenChange(o); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Last ned flere oppskrifter</DialogTitle>
          <DialogDescription>
            Last ned en kuratert samling populære norske oppskrifter fra Matprat, Bakeprosjektet, Klikk og Trines matblogg. Eksisterende oppskrifter hoppes over automatisk.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!result && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-stone-700">Antall nye oppskrifter</label>
                <div className="flex items-center gap-3">
                  {[10, 20, 30, 50].map((n) => (
                    <button
                      key={n}
                      onClick={() => setLimit(n)}
                      disabled={running}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${limit === n ? "bg-navy-800 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"} disabled:opacity-50`}
                      data-testid={`bulk-limit-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-stone-500">Tar ca. 5-15 sekunder per oppskrift (AI-parsing + bilde-nedlasting).</p>
              </div>
              {autoCfg && (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid="autoimport-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-sky-900">📅 Ukentlig auto-import</div>
                      <div className="text-xs text-sky-700 mt-0.5">
                        Hver mandag kl 07:00 UTC importerer appen {autoCfg.limit} nye oppskrifter automatisk.
                      </div>
                    </div>
                    <button
                      onClick={() => saveCfg({ enabled: !autoCfg.enabled })}
                      className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoCfg.enabled ? "bg-emerald-600" : "bg-stone-300"}`}
                      data-testid="autoimport-toggle"
                      aria-label="Slå ukentlig auto-import av/på"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoCfg.enabled ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </div>
                  {autoCfg.enabled && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-sky-700">Antall per uke:</span>
                      {[3, 5, 10, 15].map((n) => (
                        <button
                          key={n}
                          onClick={() => saveCfg({ limit: n })}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${autoCfg.limit === n ? "bg-sky-700 text-white" : "bg-white border border-sky-200 text-sky-700 hover:bg-sky-100"}`}
                          data-testid={`autoimport-weekly-${n}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                💡 Hver oppskrift parses av GPT-5.2 via Emergent LLM Key. {limit} oppskrifter bruker omtrent {Math.ceil(limit * 0.15)} kr av nøkkel-balansen.
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onOpenChange(false)}
                  disabled={running}
                  className="flex-1 rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-50"
                  data-testid="bulk-import-cancel"
                >
                  Avbryt
                </button>
                <button
                  onClick={run}
                  disabled={running}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  data-testid="bulk-import-confirm"
                >
                  {running ? (<><RefreshCw className="h-4 w-4 animate-spin" /> Laster ned...</>) : (<><Download className="h-4 w-4" /> Start nedlasting ({limit})</>)}
                </button>
              </div>
            </>
          )}
          {result && (
            <div className="space-y-3" data-testid="bulk-import-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
                  <div className="text-2xl font-bold text-emerald-700">{result.imported_count}</div>
                  <div className="text-xs text-emerald-600">Importert</div>
                </div>
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                  <div className="text-2xl font-bold text-amber-700">{result.skipped_existing || 0}</div>
                  <div className="text-xs text-amber-600">Hoppet over</div>
                </div>
                <div className="rounded-md bg-red-50 border border-red-200 p-3">
                  <div className="text-2xl font-bold text-red-700">{result.failed_count}</div>
                  <div className="text-xs text-red-600">Feilet</div>
                </div>
              </div>
              {result.imported?.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-md border border-stone-200 p-2">
                  <div className="text-xs font-semibold text-stone-600 mb-1">Nye oppskrifter:</div>
                  <ul className="text-xs text-stone-700 space-y-0.5">
                    {result.imported.map((r) => (
                      <li key={r.id} className="truncate">✅ {r.title}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.failed?.length > 0 && (
                <details className="rounded-md border border-red-200 p-2 text-xs">
                  <summary className="cursor-pointer font-semibold text-red-700">Feilede URL-er ({result.failed.length})</summary>
                  <ul className="mt-2 space-y-0.5 text-red-600">
                    {result.failed.map((f, i) => (
                      <li key={`${f.url}-${i}`} className="truncate" title={f.url}>❌ {f.url.split("/").slice(-2).join("/")} — {f.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              <button
                onClick={() => { onOpenChange(false); setResult(null); }}
                className="w-full rounded-md bg-stone-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-stone-800 transition-colors"
                data-testid="bulk-import-close"
              >
                Lukk
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
